import React, { useMemo } from 'react';
import { Box, Card, CardContent, Checkbox, FormControlLabel, Grid, Typography, Divider } from '@mui/material';
import sidebarStructure from '../../components/Sidebar/SidebarStructure';

const slugify = (s) =>
  String(s || '')
    .toLowerCase()
    .replace(/&/g, 'and')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .replace(/-{2,}/g, '-');

const makeModuleName = (groupLabel, itemLabel) => `${slugify(groupLabel)}.${slugify(itemLabel)}`;

// Build grouped modules from Sidebar structure for Real Estate + Retail sections only
function buildGroupsFromSidebar() {
  const items = Array.isArray(sidebarStructure) ? sidebarStructure : [];
  const groups = [];

  const includeSection = (label = '') => /real\s*estate/i.test(label) || /retail\s*store/i.test(label);

  const systemItems = [];

  // Traverse top-level sections
  for (const section of items) {
    if (!section || !section.label) continue;
    
    // If it's a leaf node at top level (e.g. Associate Data List), collect it
    if (!section.children && section.link && section.label !== 'Dashboard') {
      systemItems.push({ label: section.label, permission: section.permission });
      continue;
    }

    if (!includeSection(section.label)) continue;

    const childrenGroups = Array.isArray(section.children) ? section.children : [];
    for (const group of childrenGroups) {
      if (!group || !group.label) continue;
      const entries = [];
      const grandchildren = Array.isArray(group.children) ? group.children : [];
      if (grandchildren.length) {
        for (const grand of grandchildren) {
          if (grand && grand.label) entries.push({ label: grand.label, permission: grand.permission });
        }
      } else {
        entries.push({ label: group.label, permission: group.permission });
      }
      if (entries.length) groups.push({ group: group.label, items: entries });
    }
  }

  if (systemItems.length) {
    groups.unshift({ group: 'General', items: systemItems });
  }

  return groups;
}

const normalizeGroups = (modulesTree) => {
  const base = modulesTree || buildGroupsFromSidebar();
  const isGE = (s = '') => /green\s*energy/i.test(String(s));
  return (base || [])
    .map((g) => {
      if (!g) return null;
      if (isGE(g.group)) return null;
      const items = (g.items || []).filter((it) => !isGE(it?.label));
      if (!items.length) return null;
      return { ...g, items };
    })
    .filter(Boolean);
};

export const getAllModulesWithActions = (modulesTree) => {
  const groups = normalizeGroups(modulesTree);
  const actionsAll = ['read', 'write'];
  const list = [];
  groups.forEach((group) => {
    group.items.forEach((it) => {
      const name = it.permission || makeModuleName(group.group, it.label);
      list.push({ name, actions: [...actionsAll] });
    });
  });
  return list;
};

export default function ModulesRoles({ value = [], onChange, modulesTree, disabled = false }) {
  const groups = useMemo(() => normalizeGroups(modulesTree), [modulesTree]);

  const isModuleChecked = (name) => {
    return value.some((m) => (m?.name || '').toLowerCase() === String(name).toLowerCase());
  };

  const getModuleActions = (name) => {
    const mod = value.find((m) => (m?.name || '').toLowerCase() === String(name).toLowerCase());
    return mod?.actions || [];
  };

  const toggleModule = (name, checked, actionsAll) => {
    if (!onChange || disabled) return;
    if (checked) {
      // add module with all actions
      const next = value.filter((m) => (m?.name || '').toLowerCase() !== String(name).toLowerCase());
      next.push({ name, actions: [...actionsAll] });
      onChange(next);
    } else {
      // remove module
      const next = value.filter((m) => (m?.name || '').toLowerCase() !== String(name).toLowerCase());
      onChange(next);
    }
  };

  const toggleAction = (name, action, checked) => {
    if (!onChange || disabled) return;
    const idx = value.findIndex((m) => (m?.name || '').toLowerCase() === String(name).toLowerCase());
    const next = [...value];
    if (idx === -1) {
      // if module not present but an action is checked, add module with that action
      if (checked) next.push({ name, actions: [action] });
    } else {
      const actions = new Set(next[idx].actions || []);
      if (checked) actions.add(action);
      else actions.delete(action);
      const updated = Array.from(actions);
      if (updated.length === 0) {
        // drop module if no actions remain
        next.splice(idx, 1);
      } else {
        next[idx] = { name, actions: updated };
      }
    }
    onChange(next);
  };

  // Group-level helpers
  const actionsAll = ['read', 'write'];
  const namesForGroup = (group) => group.items.map((it) => it.permission || makeModuleName(group.group, it.label));
  const groupState = (group) => {
    const names = namesForGroup(group);
    if (!names.length) return { checked: false, indeterminate: false };
    let full = 0;
    let any = 0;
    for (const n of names) {
      const acts = getModuleActions(n);
      if (acts.length) any++;
      if (actionsAll.every((a) => acts.includes(a))) full++;
    }
    const checked = full === names.length;
    const indeterminate = !checked && any > 0;
    return { checked, indeterminate };
  };
  const toggleGroup = (group, checked) => {
    if (!onChange || disabled) return;
    const names = namesForGroup(group);
    let next = value.filter((m) => !names.includes(m?.name));
    if (checked) {
      next = next.concat(names.map((n) => ({ name: n, actions: [...actionsAll] })));
    }
    onChange(next);
  };

  return (
    <Box>
      {groups.map((group) => (
        <Box key={group.group} sx={{ mb: 2 }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={groupState(group).checked}
                    indeterminate={groupState(group).indeterminate}
                    onChange={(e) => toggleGroup(group, e.target.checked)}
                    disabled={disabled}
                  />
                }
                label={<Typography variant="subtitle2" sx={{ fontWeight: 700 }}>{group.group}</Typography>}
              />
          <Grid container spacing={2}>
            {group.items.map((it) => {
              const name = it.permission || makeModuleName(group.group, it.label);
              const modChecked = isModuleChecked(name);
              const selectedActions = getModuleActions(name);
              const allActionsChecked = actionsAll.every((a) => selectedActions.includes(a));
              const indeterminate = modChecked && !allActionsChecked;
              return (
                <Grid key={name} item xs={12} sm={6} md={3}>
                  <Card variant="outlined">
                    <CardContent>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={modChecked}
                            indeterminate={indeterminate}
                            onChange={(e) => toggleModule(name, e.target.checked, actionsAll)}
                            disabled={disabled}
                          />
                        }
                        label={<Typography sx={{ fontWeight: 600 }}>{it.label}</Typography>}
                      />
                      <Box sx={{ pl: 4, pt: 1 }}>
                        {actionsAll.map((a) => (
                          <FormControlLabel
                            key={a}
                            control={
                              <Checkbox
                                checked={selectedActions.includes(a)}
                                onChange={(e) => toggleAction(name, a, e.target.checked)}
                                disabled={disabled}
                              />
                            }
                            label={<Typography variant="body2" sx={{ textTransform: 'capitalize' }}>{a}</Typography>}
                          />
                        ))}
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              );
            })}
          </Grid>
          <Divider sx={{ my: 2 }} />
        </Box>
      ))}
    </Box>
  );
}
